import { ApiClientsError } from '#pplx/frontend/api-clients/core/errors.js';
import { sourcesToConnectors } from '#pplx/frontend/apps/ask/connectors-list/utilities/sourcesToConnectors.js';
import { getSourcesCatalogQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/sourcesKeys.js';
import { useApiSources } from '#pplx/frontend/apps/ask/sources/hooks/useApiSources.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { useInfiniteQuery } from '#pplx/frontend/react-query/useInfiniteQuery.js';
import { sourcesCatalogClient } from '#pplx_generated/typespec/rest/sources_catalog/client.js';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
/**
 * Hook for fetching the catalog endpoint for unauthed users on connector pages.
 */
export function useCatalogSources({ enabled, reason, searchQuery, category, popular, groupByFamily, limit = 60, noLimit, productSurface, }) {
    const pageLimit = noLimit ? undefined : limit;
    const query = useInfiniteQuery({
        queryKey: getSourcesCatalogQueryKey(searchQuery, category, popular, groupByFamily, {
            limit: pageLimit,
            noLimit,
            productSurface,
        }),
        queryFn: async ({ pageParam }) => {
            const { data, response, error } = await sourcesCatalogClient.GET('/rest/sources/catalog', reason, {
                params: {
                    query: {
                        cursor: pageParam ?? undefined,
                        limit: pageLimit,
                        no_limit: noLimit || undefined,
                        product_surface: productSurface,
                        search: searchQuery,
                        category,
                        popular,
                        group_by_family: groupByFamily || undefined,
                    },
                },
            });
            if (error || !data) {
                throw new ApiClientsError('API_CLIENTS_ERROR', {
                    message: 'Failed to fetch sources catalog',
                    cause: error,
                    status: response.status ?? 0,
                });
            }
            return data;
        },
        getNextPageParam: (lastPage) => lastPage?.next_cursor ?? undefined,
        initialPageParam: null,
        enabled,
        placeholderData: keepPreviousData,
    });
    return useMemo(() => {
        const sources = query.data?.pages.flatMap((page) => page?.sources ?? []) ?? [];
        return {
            sources,
            isLoading: query.isLoading,
            isError: query.isError,
            isFetching: query.isFetching,
            isFetchingNextPage: query.isFetchingNextPage,
            fetchNextPage: query.fetchNextPage,
            hasNextPage: query.hasNextPage,
            isRefetching: query.isRefetching,
        };
    }, [
        query.data,
        query.isLoading,
        query.isError,
        query.isFetching,
        query.isFetchingNextPage,
        query.fetchNextPage,
        query.hasNextPage,
        query.isRefetching,
    ]);
}
export function useConnectors({ enabled, reason, filterBy, searchQuery, noLimit, category, popular, custom, useCatalog, includePremiumSources, excludeIneligible, groupByFamily, productSurface, }) {
    const isLoggedIn = useIsUserLoggedIn();
    const { hasActiveSubscription } = useIsSubscribed();
    // Unauthed users on connector pages → catalog endpoint
    const usesCatalog = useCatalog === true && !isLoggedIn;
    // Free logged-in users on connector pages → /rest/sources with browse_all
    const catalogMode = useCatalog === true && isLoggedIn && !hasActiveSubscription;
    const catalog = useCatalogSources({
        enabled: usesCatalog && enabled,
        reason,
        searchQuery,
        category,
        popular,
        groupByFamily,
    });
    const apiFilterBy = filterBy === 'installed' ? 'connected' : filterBy === 'available' ? 'disconnected' : undefined;
    const { sources, isSourcesLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, isRefetching, } = useApiSources({
        enabled: !usesCatalog && enabled,
        reason,
        searchQuery,
        filterBy: apiFilterBy,
        limit: 60,
        noLimit,
        category,
        popular,
        custom,
        catalogMode,
        includePremiumSources,
        excludeIneligible,
        groupByFamily,
        productSurface,
    });
    return useMemo(() => {
        if (usesCatalog) {
            return {
                connectors: sourcesToConnectors(catalog.sources),
                isLoading: catalog.isLoading,
                isFetching: catalog.isFetching,
                isFetchingNextPage: catalog.isFetchingNextPage,
                fetchNextPage: catalog.fetchNextPage,
                hasNextPage: catalog.hasNextPage,
                isRefetching: catalog.isRefetching,
            };
        }
        return {
            connectors: sourcesToConnectors(sources),
            isLoading: isSourcesLoading,
            isFetching,
            isFetchingNextPage: isFetchingNextPage,
            fetchNextPage,
            hasNextPage,
            isRefetching,
        };
    }, [
        usesCatalog,
        catalog,
        sources,
        isSourcesLoading,
        isFetching,
        isFetchingNextPage,
        isRefetching,
        fetchNextPage,
        hasNextPage,
    ]);
}
//# sourceMappingURL=useConnectors.js.map